/**
 * ApiDocsPage — S10-14
 *
 * Route: /api-docs
 * API documentation page with OpenAPI spec accordion view
 * Shows endpoints with method badge, path, summary
 * Expanded view: description, parameters, request/response schema, curl example
 *
 * The OpenAPI spec is fetched dynamically from /openapi.json (public folder)
 * to avoid bloating the initial bundle.
 */
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Copy, Check, FileText, Lock, Zap, Loader2 } from 'lucide-react'
import { Accordion, AccordionItem } from '../../components/Accordion'
import { ApiQuotaCard } from '../developer/ApiQuotaCard'
import { ApiKeyCard } from '../developer/ApiKeyCard'
import { PublicApiCatalog } from './PublicApiCatalog'
import type { EndpointInfo, HttpMethod } from './types'
import { METHOD_COLORS } from './types'
import {
  generateCurl,
  getDefaultHeaders,
  formatPathForDisplay,
  extractEndpoints,
} from './curlGenerator'

type OpenAPISpec = {
  paths: Record<string, unknown>
  info?: { title?: string; version?: string; description?: string }
}

function MethodBadge({ method }: { method: HttpMethod }) {
  const colors = METHOD_COLORS[method.toUpperCase() as HttpMethod]
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'monospace',
        backgroundColor: colors.background,
        color: colors.text,
        letterSpacing: '0.05em',
      }}
    >
      {method}
    </span>
  )
}

function EndpointAccordionItem({
  endpoint,
  isInitiallyOpen = false,
}: {
  endpoint: EndpointInfo
  isInitiallyOpen?: boolean
}) {
  const { t } = useTranslation('apidocs')
  const [copied, setCopied] = useState(false)

  const curlCommand = useMemo(() => {
    const headers = getDefaultHeaders(endpoint.security)
    return generateCurl(endpoint.method, endpoint.path, headers)
  }, [endpoint])

  const handleCopyCurl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(curlCommand)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = curlCommand
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [curlCommand])

  const title = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <MethodBadge method={endpoint.method} />
      <code style={{ fontSize: 13, color: 'var(--text)', fontFamily: 'monospace' }}>
        {formatPathForDisplay(endpoint.path)}
      </code>
      {endpoint.isPremium && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            backgroundColor: 'var(--primary-alpha-10)',
            color: 'var(--primary)',
          }}
        >
          <Zap size={10} /> Premium
        </span>
      )}
      {endpoint.security && endpoint.security.length > 0 && !endpoint.security.some((s) => Object.keys(s).length === 0) && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            backgroundColor: 'var(--warning-alpha-10)',
            color: 'var(--warning)',
          }}
        >
          <Lock size={10} /> Auth
        </span>
      )}
      {endpoint.summary && (
        <span style={{ color: 'var(--text-subtle)', fontSize: 13 }}>{endpoint.summary}</span>
      )}
    </div>
  )

  return (
    <AccordionItem id={`endpoint-${endpoint.operationId || `${endpoint.method}-${endpoint.path}`}`} title={title} isOpen={isInitiallyOpen}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Description */}
        {endpoint.description && (
          <div>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 13, fontWeight: 600 }}>{t('endpoint.description')}</h4>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-subtle)', lineHeight: 1.6 }}>
              {endpoint.description}
            </p>
          </div>
        )}

        {/* Tags */}
        {endpoint.tags && endpoint.tags.length > 0 && (
          <div>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 13, fontWeight: 600 }}>{t('endpoint.tags')}</h4>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {endpoint.tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    backgroundColor: 'var(--bg-elev)',
                    color: 'var(--text-subtle)',
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Parameters */}
        {endpoint.parameters && endpoint.parameters.length > 0 && (
          <div>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 13, fontWeight: 600 }}>{t('endpoint.parameters')}</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-subtle)' }}>{t('endpoint.params.name')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-subtle)' }}>{t('endpoint.params.in')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-subtle)' }}>{t('endpoint.params.type')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-subtle)' }}>{t('endpoint.params.required')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-subtle)' }}>{t('endpoint.params.description')}</th>
                </tr>
              </thead>
              <tbody>
                {endpoint.parameters.map((param) => (
                  <tr key={param.name} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>{param.name}</td>
                    <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text-subtle)' }}>{param.in}</td>
                    <td style={{ padding: '6px 8px', fontSize: 11, fontFamily: 'monospace' }}>{param.schema?.type || 'string'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 11 }}>{param.required ? t('endpoint.params.yes') : t('endpoint.params.no')}</td>
                    <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text-subtle)' }}>{param.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Curl Example */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{t('endpoint.curlExample')}</h4>
            <button
              onClick={handleCopyCurl}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 500,
                backgroundColor: copied ? 'var(--success-alpha-10)' : 'var(--bg-elev)',
                color: copied ? 'var(--success)' : 'var(--text)',
                border: '1px solid var(--border)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              aria-label={t('endpoint.copyAriaLabel')}
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? t('endpoint.copied') : t('endpoint.copy')}
            </button>
          </div>
          <pre
            style={{
              margin: 0,
              padding: 12,
              backgroundColor: 'var(--bg-elev)',
              borderRadius: 6,
              fontSize: 11,
              fontFamily: 'monospace',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              border: '1px solid var(--border)',
            }}
          >
            {curlCommand}
          </pre>
        </div>
      </div>
    </AccordionItem>
  )
}

export function ApiDocsPage() {
  const { t } = useTranslation('apidocs')
  const [filter, setFilter] = useState('')
  const [spec, setSpec] = useState<OpenAPISpec | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch the OpenAPI spec lazily from public folder
  useEffect(() => {
    async function fetchSpec() {
      try {
        const response = await fetch('/openapi.json')
        if (!response.ok) {
          throw new Error(`Failed to load OpenAPI spec: ${response.status}`)
        }
        const data = await response.json()
        setSpec(data as OpenAPISpec)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('error.loadFailed'))
      } finally {
        setLoading(false)
      }
    }
    void fetchSpec()
  }, [t])

  const endpoints = useMemo(() => {
    if (!spec?.paths) return []
    return extractEndpoints(spec as { paths: Record<string, unknown> })
  }, [spec])

  const filteredEndpoints = useMemo(() => {
    if (!filter.trim()) return endpoints

    const searchTerm = filter.toLowerCase()
    return endpoints.filter(
      (ep) =>
        ep.path.toLowerCase().includes(searchTerm) ||
        ep.method.toLowerCase().includes(searchTerm) ||
        ep.summary?.toLowerCase().includes(searchTerm) ||
        ep.tags?.some((tag) => tag.toLowerCase().includes(searchTerm))
    )
  }, [endpoints, filter])

  // Group endpoints by first path segment for better organization
  const groupedEndpoints = useMemo(() => {
    const groups: Record<string, EndpointInfo[]> = {}
    for (const endpoint of filteredEndpoints) {
      const match = endpoint.path.match(/^\/api\/v2\/([^/]+)/)
      const group = match ? match[1] : 'Other'
      if (!groups[group]) groups[group] = []
      groups[group].push(endpoint)
    }
    return groups
  }, [filteredEndpoints])

  // R3-S3: Kontingent-Karte, API-Key und der v1-Endpunkt-Katalog haengen NICHT
  // an der OpenAPI-Spec — sie werden auch bei Lade-/Fehlerzustand der Spec
  // gerendert (v1 hatte beides auf derselben Seite /api).
  const staticSections = (
    <>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <FileText size={24} style={{ color: 'var(--primary)' }} />
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{t('page.title')}</h1>
        </div>
      </div>

      <ApiQuotaCard />
      <ApiKeyCard />
      <PublicApiCatalog />
    </>
  )

  // Loading state (nur der v2-Spec-Teil)
  if (loading) {
    return (
      <div>
        {staticSections}
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Loader2
            size={32}
            style={{
              color: 'var(--primary)',
              animation: 'ht-spin 1s linear infinite',
              marginBottom: 16,
            }}
          />
          <p style={{ color: 'var(--text-subtle)', fontSize: 14 }}>{t('loading')}</p>
        </div>
      </div>
    )
  }

  // Error state (nur der v2-Spec-Teil)
  if (error || !spec) {
    return (
      <div>
        {staticSections}
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ color: 'var(--error)', fontSize: 14, marginBottom: 16 }}>
            {error || t('error.specFailed')}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {t('endpoint.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      {staticSections}

      {/* v2-Spec-Header */}
      <div style={{ marginBottom: 24 }}>
        <h2
          style={{
            margin: '0 0 6px',
            fontSize: 18,
            fontWeight: 700,
            borderBottom: '1px solid var(--border)',
            paddingBottom: 8,
          }}
        >
          {t('v2Api.title')}
        </h2>
        {spec.info?.title && (
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-subtle)', fontSize: 14 }}>
            {spec.info.title} {spec.info.version && `${spec.info.version}`}
          </p>
        )}
        {spec.info?.description && (
          <p style={{ margin: '8px 0 0 0', color: 'var(--text-subtle)', fontSize: 13 }}>
            {spec.info.description}
          </p>
        )}
      </div>

      {/* Search Filter */}
      <div style={{ marginBottom: 24, position: 'relative', maxWidth: 400 }}>
        <Search
          size={14}
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-subtle)',
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        />
        <input
          type="search"
          placeholder={t('filter.placeholder')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input"
          style={{ paddingLeft: 36, width: '100%' }}
          aria-label={t('filter.ariaLabel')}
        />
        {filter && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-subtle)' }}>
            {t('filter.showing', { filtered: filteredEndpoints.length, total: endpoints.length })}
          </div>
        )}
      </div>

      {/* Endpoints by Group */}
      {Object.entries(groupedEndpoints).map(([group, groupEndpoints]) => (
        <div key={group} style={{ marginBottom: 24 }}>
          <h2
            style={{
              margin: '0 0 12px 0',
              fontSize: 16,
              fontWeight: 600,
              textTransform: 'capitalize',
              color: 'var(--text)',
              borderBottom: '1px solid var(--border)',
              paddingBottom: 8,
            }}
          >
            {group.replace(/-/g, ' ')}
          </h2>
          <Accordion>
            {groupEndpoints.map((endpoint, index) => (
              <EndpointAccordionItem
                key={`${endpoint.method}-${endpoint.path}`}
                endpoint={endpoint}
                isInitiallyOpen={index === 0 && filter === ''}
              />
            ))}
          </Accordion>
        </div>
      ))}

      {/* Empty State */}
      {filteredEndpoints.length === 0 && filter && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: 'var(--text-subtle)', fontSize: 14 }}>
            {t('filter.noResults', { query: filter })}
          </p>
          <button
            onClick={() => setFilter('')}
            style={{
              marginTop: 12,
              padding: '8px 16px',
              backgroundColor: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {t('filter.clear')}
          </button>
        </div>
      )}
    </div>
  )
}
